import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

/**
 * Org members (assignable users) — loaded once and shared, so member/assignee
 * fields can render a picker and display names/avatars without each component
 * refetching.
 */
const MembersContext = createContext({ members: [], usersById: {}, refresh: () => {} });

export function MembersProvider({ children }) {
  const [members, setMembers] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/members");
      setMembers(Array.isArray(r.data) ? r.data : []);
    } catch { /* not signed in yet — ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const usersById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.user_id, m])),
    [members],
  );

  return (
    <MembersContext.Provider value={{ members, usersById, refresh }}>
      {children}
    </MembersContext.Provider>
  );
}

export function useMembers() {
  return useContext(MembersContext);
}

export function MemberAvatar({ user, size = 20 }) {
  const initials = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
  return (
    <span
      className="rounded-full bg-primary/15 text-primary font-semibold flex items-center justify-center overflow-hidden shrink-0"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }}
    >
      {user?.avatar_url
        ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
        : initials}
    </span>
  );
}

/** Inline display of an assignee value (a user id). */
export function MemberChip({ userId }) {
  const { usersById } = useMembers();
  if (!userId) return <span className="text-muted-foreground">—</span>;
  const u = usersById[userId];
  return (
    <span className="inline-flex items-center gap-1.5 max-w-full">
      <MemberAvatar user={u} size={20} />
      <span className="truncate">{u?.name || u?.email || "Unknown member"}</span>
    </span>
  );
}
